import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CalendarClockIcon } from 'lucide-react';
import { Button, EmptyState, ErrorNotice, FactList, Field, Panel, TextInput } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  isAllowedSchedulingUrl,
  MEETING_DURATIONS,
  updateMyProfile,
  updateUser,
  type MeetingDuration,
  type PersonProfile,
  type SchedulingLink,
} from '@/lib/events';

/** Roles that own website leads, so a scheduling link means something on their profile. */
export const SCHEDULING_LINK_ROLES: ReadonlySet<string> = new Set(['internal', 'admin', 'superadmin']);

/** The example shown under each field. */
const EXAMPLE_LINK = (minutes: MeetingDuration) => `For example https://calendly.com/your-name/${minutes}min`;

type Drafts = Readonly<Record<MeetingDuration, string>>;

/** The stored links as one text value per call length ('' when none). */
function toDrafts(links: readonly SchedulingLink[] | undefined): Drafts {
  const byLength = (minutes: MeetingDuration) =>
    links?.find((link) => link.durationMinutes === minutes)?.url ?? '';
  return { 10: byLength(10), 15: byLength(15) };
}

/** The filled-in drafts as the API's `schedulingLinks` (a blank field removes that link). */
export function toSchedulingLinks(drafts: Drafts): SchedulingLink[] {
  return MEETING_DURATIONS.flatMap((minutes) => {
    const url = drafts[minutes].trim();
    return url ? [{ durationMinutes: minutes, url }] : [];
  });
}

/** Why a draft cannot be saved, or undefined when it can (blank is fine). */
export function schedulingLinkError(value: string): string | undefined {
  const url = value.trim();
  return url === '' || isAllowedSchedulingUrl(url) ? undefined : 'Use an https://calendly.com link.';
}

export interface SchedulingLinksSectionProps {
  readonly person: PersonProfile;
  /** 'self' saves through PUT /users/me, 'admin' through PUT /users/:id; absent is read-only. */
  readonly mode?: 'self' | 'admin';
  readonly onSaved: () => void;
}

/**
 * The profile's call-booking links: one Calendly link per call length. A
 * website lead this person owns is offered the one the "Offer a call with the
 * Lead owner" setting names, in the thank-you email and on the thank-you page.
 *
 * @param props - The person, who is editing (if anyone), and the saved handler.
 * @returns The scheduling links section.
 */
export function SchedulingLinksSection({ person, mode, onSaved }: SchedulingLinksSectionProps): React.ReactElement {
  const stored = toDrafts(person.schedulingLinks);
  const [drafts, setDrafts] = useState<Drafts | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const values = drafts ?? stored;
  const errors = { 10: schedulingLinkError(values[10]), 15: schedulingLinkError(values[15]) } as const;
  const invalid = MEETING_DURATIONS.some((minutes) => errors[minutes] !== undefined);
  const dirty = MEETING_DURATIONS.some((minutes) => values[minutes].trim() !== stored[minutes]);

  const save = useMutation({
    mutationFn: () => {
      const patch = { schedulingLinks: toSchedulingLinks(values) };
      return mode === 'admin' ? updateUser(person._id, patch) : updateMyProfile(patch);
    },
    onSuccess: () => {
      setDrafts(undefined);
      setSaved(true);
      onSaved();
    },
  });

  const heading = (
    <>
      <h2 className="mb-1 text-portal-section font-bold tracking-[-0.4px]">Scheduling links</h2>
      <p className="mb-3 text-portal-small text-portal-muted">
        Website leads {mode === 'self' ? 'you own' : 'this person owns'} can book a short call on these links.
      </p>
    </>
  );

  if (!mode) {
    const links = toSchedulingLinks(stored);
    return (
      <div className="mt-6">
        {heading}
        {links.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<CalendarClockIcon aria-hidden className="size-6" />}
              title="No scheduling links"
              message="Calendly links this person adds to their profile will appear here."
            />
          </Panel>
        ) : (
          <Panel className="p-5">
            <FactList
              columns={2}
              facts={links.map((link) => ({ label: `${link.durationMinutes}-minute call`, value: link.url }))}
            />
          </Panel>
        )}
      </div>
    );
  }

  return (
    <div className="mt-6">
      {heading}
      <Panel className="p-5">
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!invalid) save.mutate();
          }}
        >
          {MEETING_DURATIONS.map((minutes) => {
            const id = `scheduling-link-${minutes}`;
            return (
              <Field
                key={minutes}
                label={`${minutes}-minute call link`}
                htmlFor={id}
                hint={errors[minutes] ? undefined : EXAMPLE_LINK(minutes)}
              >
                <TextInput
                  id={id}
                  type="url"
                  inputMode="url"
                  placeholder={`https://calendly.com/your-name/${minutes}min`}
                  value={values[minutes]}
                  aria-invalid={errors[minutes] !== undefined}
                  disabled={save.isPending}
                  onChange={(event) => {
                    setSaved(false);
                    setDrafts({ ...values, [minutes]: event.target.value });
                  }}
                />
                {errors[minutes] ? (
                  <p className="mt-1 text-portal-micro font-semibold text-red-600">{errors[minutes]}</p>
                ) : null}
              </Field>
            );
          })}
          <div className="flex items-center gap-3 sm:col-span-2">
            <Button type="submit" disabled={save.isPending || !dirty || invalid}>
              {save.isPending ? 'Saving…' : 'Save links'}
            </Button>
            {saved ? <span className="text-portal-micro text-portal-muted">Saved.</span> : null}
          </div>
        </form>
        {save.isError ? (
          <div className="mt-3">
            <ErrorNotice inline title="Links not saved" message={errorMessage(save.error)} />
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
